import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import OptionSection from 'src/js/modules/common/components/option-section';
import { INDETERMINATE } from '../constants/common';
import ElementSettingsContext from '../helpers/element-settings-context';
import getBatchValues from '../helpers/get-batch-values';

const { Box, SimpleSelect } = designSystem;

function SelectControl({
  id,
  elementIds,
  targets,
  title,
  choices,
  popover = null,
  mixedLabel = null,
  nested = false,
  computeFallbackValue = null,
  onChange,
}) {
  const { resolvedState } = useContext(ElementSettingsContext);

  const values = getBatchValues(resolvedState, { elementIds, targets, computeFallbackValue });
  const flattenedValues = [values[0]].flat();
  const currentValue = flattenedValues[0];
  const isMixed = Boolean(mixedLabel) && flattenedValues.some((value) => value !== currentValue);
  const adaptedOptions = choices.map((el) => ({
    label: el.name,
    value: el.value,
  }));
  const selectedValue = isMixed
    ? { label: mixedLabel, value: INDETERMINATE }
    : adaptedOptions.find((el) => el.value === currentValue) || adaptedOptions[0];

  const handleChange = useCallback(
    ({ value }) => {
      onChange({ controlId: id, elementIds, targets, value });
    },
    [id, elementIds, targets, onChange]
  );

  return (
    <Box
      {...(!nested && {
        paddingBottom: 'spacing-xs',
        paddingLeft: 'spacing-m',
        paddingRight: 'spacing-m',
      })}
    >
      <OptionSection title={title} popoverProps={popover} bold={!nested}>
        <SimpleSelect
          value={selectedValue}
          options={adaptedOptions}
          menuWidth="auto"
          transparent
          noVerticalSpacing
          noHorizontalSpacing
          noSpacingBetweenElements
          onChange={handleChange}
        />
      </OptionSection>
    </Box>
  );
}

SelectControl.propTypes = {
  id: PropTypes.string.isRequired,
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  choices: PropTypes.array.isRequired,
  popover: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    imgSrc: PropTypes.string,
  }),
  mixedLabel: PropTypes.string,
  nested: PropTypes.bool,
  computeFallbackValue: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

export default SelectControl;
